import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { PlatformNotification } from './types';

interface NotificationCenterContextValue {
  notifications: PlatformNotification[];
  count: number;
  unreadCount: number;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  registerSource: (
    sourceId: string,
    notifications: PlatformNotification[],
  ) => void;
  unregisterSource: (sourceId: string) => void;
}

const NotificationCenterContext =
  createContext<NotificationCenterContextValue | null>(null);

const getCreatedAtMs = (notification: PlatformNotification) => {
  const timestamp = Date.parse(notification.createdAt);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export function NotificationCenterProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [sources, setSources] = useState<
    Record<string, PlatformNotification[]>
  >({});
  const [isOpen, setOpen] = useState(false);

  const registerSource = useCallback(
    (sourceId: string, notifications: PlatformNotification[]) => {
      setSources((current) => ({ ...current, [sourceId]: notifications }));
    },
    [],
  );

  const unregisterSource = useCallback((sourceId: string) => {
    setSources((current) => {
      if (!(sourceId in current)) return current;
      const next = { ...current };
      delete next[sourceId];
      return next;
    });
  }, []);

  const notifications = useMemo(
    () =>
      Object.values(sources)
        .flat()
        .sort((a, b) => getCreatedAtMs(b) - getCreatedAtMs(a)),
    [sources],
  );

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.isRead).length,
    [notifications],
  );

  // Nothing left to show: collapse the tray so it doesn't reopen on its own when
  // the next notification arrives (isOpen lives here, not in the unmounting tray).
  useEffect(() => {
    if (notifications.length === 0) setOpen(false);
  }, [notifications.length]);

  const value = useMemo<NotificationCenterContextValue>(
    () => ({
      notifications,
      count: notifications.length,
      unreadCount,
      isOpen,
      setOpen,
      registerSource,
      unregisterSource,
    }),
    [notifications, unreadCount, isOpen, registerSource, unregisterSource],
  );

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  );
}

const useNotificationCenterContext = () => {
  const context = useContext(NotificationCenterContext);
  if (!context) {
    throw new Error(
      'useNotificationCenter must be used within a NotificationCenterProvider',
    );
  }
  return context;
};

export const useNotificationCenter = () => {
  const { notifications, count, unreadCount, isOpen, setOpen } =
    useNotificationCenterContext();
  return { notifications, count, unreadCount, isOpen, setOpen };
};

export const useNotificationCenterActions = () => {
  const { setOpen } = useNotificationCenterContext();
  const open = useCallback(() => setOpen(true), [setOpen]);
  return { open };
};

export const useRegisterNotificationSource = (
  sourceId: string,
  notifications: PlatformNotification[],
) => {
  const { registerSource, unregisterSource } = useNotificationCenterContext();

  // Register (and refresh) whenever the source's notifications change. Kept
  // separate from the unmount cleanup so a new notifications reference — e.g. a
  // fresh array from a poll — updates the source in place instead of briefly
  // unregistering it, which would drop the count to zero and flicker the tray.
  useEffect(() => {
    registerSource(sourceId, notifications);
  }, [sourceId, notifications, registerSource]);

  useEffect(
    () => () => unregisterSource(sourceId),
    [sourceId, unregisterSource],
  );
};
